import { useLocalization } from '@components/Localization'
import { Page, pickSeoProps } from '@components/Page'
import FamilyOverview from '@components/_rolex/FamilyOverview'
import Hero from '@components/_rolex/Hero'
import RolexPage from '@components/_rolex/Page'
import KeepExploring from '@components/_rolex/Sliders/KeepExploring'
import TextBlock from '@components/_rolex/TextBlock'
import { Box } from '@gassan-ui'
import { MultipleItemResponse, SingleItemResponse } from '@lib/kontent'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { getRolexProducts } from '@lib/kontent/getRolexProducts'
import { RolexFamily } from '@lib/kontent/models/RolexFamily'
import { RolexProduct } from '@lib/kontent/models/RolexProduct'
import { PageProps } from '@lib/types'
import ErrorPage from '@pages/_error'
import { Button, RolexContainer } from '@rolex-ui'
import { NextPage } from 'next'
import { rem } from 'polished'
import React from 'react'

const translateMap = {
  'dames-horloges': 'womens-watches',
  'heren-horloges': 'mens-watches',
  'gouden-horloges': 'golden-watches'
}

const translateMapReverse = {
  'womens-watches': 'dames-horloges',
  'mens-watches': 'heren-horloges',
  'golden-watches': 'gouden-horloges'
}

type Props = PageProps<{
  content: SingleItemResponse<RolexFamily>
  items: MultipleItemResponse<RolexProduct>
}> & {
  style?: string[]
  size?: string[]
  material?: string[]
  dial?: string[]
  page?: number
  fid?: string
}

const RolexOverview: NextPage<Props> = ({ data, statusCode, fid, ...rest }) => {
  const { lang, messages } = useLocalization()
  const keepExploringItems = data.content.item.elements.keep_exploring_items.value.map(
    key => data.content.modular_content[key].elements
  )
  const translatedFid = translateMap[fid] || fid
  if (statusCode) {
    return <ErrorPage statusCode={statusCode} />
  }
  if (!data || !data.content) {
    return <ErrorPage statusCode={404} />
  }

  const sources: string[] = [
    data.content.item.elements.rolex_hero__mobile?.value[0]?.url,
    data.content.item.elements.rolex_hero__mobile?.value[0]?.url,
    data.content.item.elements.rolex_hero__desktop?.value[0]?.url
  ].filter(t => t)

  return (
    <Page
      {...pickSeoProps(data.content.item.elements)}
      slug={`rolex/${fid}`}
      languageAlternates={[
        { hrefLang: 'nl', href: `/nl/rolex/${translateMapReverse[fid] || fid}` },
        { hrefLang: 'en', href: `/en/rolex/${translatedFid}` }
      ]}
    >
      <RolexPage>
        <Hero sources={sources} alt={`Rolex - ${data.content.item.elements.title.value}`} />
        <RolexContainer py="3.75rem">
          <TextBlock
            preTitle={data.content.item.elements.pre_title.value}
            title={data.content.item.elements.title.value}
            description={data.content.item.elements.body.value}
          >
            <Box mt={[rem(20), rem(30)]}>
              <Button as="link" href={`/${lang}/rolex/contact`}>
                {messages.rolex.navigation.contactUs}
              </Button>
            </Box>
          </TextBlock>
        </RolexContainer>
        <FamilyOverview fid={translatedFid} initialData={data.items} {...rest} />
        <KeepExploring
          items={keepExploringItems}
          title={lang === 'nl' ? 'Blijf ontdekken' : 'Keep exploring'}
        />
      </RolexPage>
    </Page>
  )
}

RolexOverview.getInitialProps = async ({ lang, query }) => {
  const fid = query.fid as string
  const translatedFid = translateMap[fid] || fid
  const language = lang === 'sg' ? 'en' : lang
  let args = {}
  if (query['style']) {
    args['style'] = (query.style as string).split(',')
  }

  if (query['size']) {
    args['size'] = (query.size as string).split(',')
  }
  if (query['material']) {
    args['material'] = (query.material as string).split(',')
  }

  if (query['dial']) {
    args['dial'] = (query.dial as string).split(',')
  }
  if (query['page']) {
    args['page'] = query['page'] ? parseInt(query['page'] as string, 10) : 1
  }

  // Overwrite query if we're on an overview page
  if (translatedFid === 'womens-watches') {
    args['style'] = ['women']
  }
  if (translatedFid === 'mens-watches') {
    args['style'] = ['men']
  }
  if (translatedFid === 'golden-watches') {
    args['material'] = ['gold']
  }

  const content: SingleItemResponse<RolexFamily> = await getPageByTypeAndUrlSlug({
    type: 'rolex_family',
    urlSlug: fid,
    language
  })
  const items = await getRolexProducts({
    language,
    family: translateMap[fid] || fid,
    ...args
  })

  return {
    fid,
    data: {
      content,
      items,
      keepExploring: []
    },
    ...args
  }
}

export default RolexOverview
